import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, Drawer, AppBar, Toolbar, Typography, CssBaseline, IconButton, Tooltip } from '@mui/material';
import { Brightness4, Brightness7 } from '@mui/icons-material';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useThemeMode } from './context/ThemeContext';
import Menu from './components/Menu';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import MarketplaceSetupPage from './pages/MarketplaceSetupPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import Dashboard from './pages/Dashboard';
import Placeholder from './pages/Placeholder';
import ApiKeysPage from './pages/ApiKeysPage';
import ApiExplorerGuide from './pages/ApiExplorerGuide';
import RolesPermissionsPage from './pages/admin/RolesPermissionsPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import DetachRequestsPage from './pages/admin/DetachRequestsPage';
import Header from './components/Header';
import LandingPage from './components/LandingPage';
import './App.css';

const DRAWER_WIDTH = 260;

function HomeRedirect() {
  const { user, loading } = useAuth();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const hasMarketplaceHandoff =
    searchParams.has('marketplaceSetup') ||
    searchParams.has('marketplaceSessionId') ||
    searchParams.has('x-amzn-marketplace-token') ||
    searchParams.has('x-amzn-marketplace-offer-type') ||
    searchParams.has('registrationToken');

  if (hasMarketplaceHandoff) {
    return <Navigate to={`/setup${location.search}`} replace />;
  }

  if (loading) return null;
  return user ? <Navigate to="/dashboard" replace /> : <LandingPage />;
}

function AppLayout() {
  const { user } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const location = useLocation();
  const isPublicPage = location.pathname === '/';

  return (
    <Box sx={{ display: 'flex' }}>
      {!isPublicPage && (
        <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
          <Toolbar>
            <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>VIC Services API Management</Typography>
            <Tooltip title={mode === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode'}>
              <IconButton onClick={toggleTheme} color="inherit">
                {mode === 'light' ? <Brightness4 /> : <Brightness7 />}
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>
      )}
      {!isPublicPage && user && (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          <Toolbar />
          <Menu />
        </Drawer>
      )}
      <Box component="main" sx={{ flexGrow: 1, p: isPublicPage ? 0 : 3 }}>
        {!isPublicPage && <Toolbar />}
        {!isPublicPage && user && <Header />}
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/setup" element={<MarketplaceSetupPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/dashboard" element={<ProtectedRoute requiredRole="user"><Dashboard /></ProtectedRoute>} />
          <Route path="/api-keys" element={<ProtectedRoute requiredRole="user"><ApiKeysPage /></ProtectedRoute>} />
          <Route path="/api-explorer" element={<ProtectedRoute requiredRole="user"><ApiExplorerGuide /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute requiredRole="user"><Placeholder title="Analytics" /></ProtectedRoute>} />
          <Route path="/admin/dashboard" element={<ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/roles" element={<ProtectedRoute requiredRole="admin"><RolesPermissionsPage /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute requiredRole="admin"><AdminSettingsPage /></ProtectedRoute>} />
          <Route path="/admin/detach-requests" element={<ProtectedRoute requiredRole="admin"><DetachRequestsPage /></ProtectedRoute>} />
        </Routes>
      </Box>
    </Box>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <CssBaseline />
        <AuthProvider>
          <AppLayout />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}